import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  chatMessageVariablesQuery,
  chatMessagesQuery,
  chatMessagesShellQuery,
  createChatMessage,
  removeChatMessage,
  setChatMessagesActive,
  updateChatMessage,
  type ChatMessage,
} from '../../api/settingsEaseedeskChat'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Languages (mesajele automate din chat).
 *
 * Element cu element fata de `version-3/easeedesk_settings/chat/settings/
 * messages_languages.php` + `messages_list.php`, si gest cu gest fata de
 * `messages_languages-1.js` si `messages_list-1.js`.
 *
 * LISTA DE OPTIUNI din dreapta casutei de mesaj e chiar
 * `message_variables()`: variabilele felului de mesaj ales. Se apasa pe una si
 * intra in text, ca in portal.
 */

interface MessageForm {
  company: number
  language: number
  message_type: number
  message_body: string
  message_delay: number
}

const EMPTY: MessageForm = { company: 0, language: 0, message_type: 0, message_body: '', message_delay: 0 }

/**
 * Randul de sub fiecare mesaj, ca in macheta: `strlen($mesaj) > 100 ?
 * substr(strip_tags($mesaj), 0, 100) . '...' : strip_tags($mesaj)`.
 *
 * Etichetele se TAIE: mesajele pot veni cu HTML in ele, si fara taiere randul
 * din lista incepea cu `<p>`. Masura se ia pe textul brut, ca in portal.
 */
function excerpt(body: string): string {
  const text = body.replace(/<[^>]*>/g, '')
  return body.length > 100 ? `${text.slice(0, 100)}...` : text
}

/** Variabilele felului de mesaj ales; se pun in text la apasare. */
function VariableList({ type, onPick }: { type: number; onPick(variable: string): void }) {
  const data = useQuery(chatMessageVariablesQuery(type))
  const variables = Object.entries(data.data?.variables ?? {})

  return (
    <>
      <h5>Options list</h5>
      <hr className="dotted" />
      <ul style={{ height: 'auto', width: 'auto', overflowY: 'auto' }} className="message_list">
        {variables.map(([variable, name]) => (
          <li key={variable}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault()
                onPick(variable)
              }}
            >
              {name}
            </a>
          </li>
        ))}
      </ul>
    </>
  )
}

function MessageModal({
  message,
  open,
  onClose,
  onSaved,
  companies,
  languages,
  types,
}: {
  message?: ChatMessage
  open: boolean
  onClose(): void
  onSaved(): void
  companies: Array<{ ID: number; name: string }>
  languages: Array<{ id: number; language_name: string }>
  types: Array<{ id: number; type_name: string }>
}) {
  const editing = message !== undefined
  const [form, setForm] = useState<MessageForm>(EMPTY)

  useEffect(() => {
    if (!open) return
    setForm(
      editing
        ? {
            company: message.company_id,
            language: message.language_id,
            message_type: message.message_type,
            message_body: message.message,
            message_delay: message.message_delay,
          }
        : { ...EMPTY, language: languages[0]?.id ?? 0 },
    )
  }, [open, editing, message, languages])

  const save = useMutation({
    mutationFn: () => (editing ? updateChatMessage(message.id, form) : createChatMessage(form)),
    onSuccess: () => {
      toast(editing ? 'Message updated.' : 'Message created.', 'Languages')
      onClose()
      onSaved()
    },
    onError: (e) =>
      toast(e instanceof ApiError && e.message ? e.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id={editing ? 'editMessageModal' : 'newMessagesModal'}
      labelledBy={editing ? 'editMessageModalLabel' : 'newMessagesModalLabel'}
      open={open}
      onClose={onClose}
      title={editing ? 'Edit message' : 'New message'}
    >
      <form
        id={editing ? 'edit-message-form' : 'new-message-form'}
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          save.mutate()
        }}
      >
        <div className="row">
          <div className="form-group col-md-6">
            <label className="control-label">
              Company <span className="required">*</span>
            </label>
            <select
              className="form-control input-sm"
              name="company"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: Number(e.target.value) })}
            >
              <option value={0}>All companies</option>
              {companies.map((row) => (
                <option key={row.ID} value={row.ID}>
                  {row.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group col-md-6">
            <label className="control-label">
              Language <span className="required">*</span>
            </label>
            <select
              className="form-control input-sm"
              name="language"
              value={form.language}
              onChange={(e) => setForm({ ...form, language: Number(e.target.value) })}
              required
            >
              {languages.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.language_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group col-md-6">
            <label className="control-label">
              Message type <span className="required">*</span>
            </label>
            <select
              className="form-control input-sm select-event"
              name="message_type"
              value={form.message_type}
              onChange={(e) => setForm({ ...form, message_type: Number(e.target.value) })}
            >
              <option value={0}>Select message type</option>
              {types.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.type_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group col-md-6">
            <label className="control-label">Message Delay</label>
            <input
              type="number"
              className="form-control input-sm"
              name="message_delay"
              value={form.message_delay}
              onChange={(e) => setForm({ ...form, message_delay: Number(e.target.value) })}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="control-label">Message body</label>
          <div className="row">
            <div className="col-md-8">
              <input
                type="text"
                name="message_body"
                className="message-body form-control"
                value={form.message_body}
                onChange={(e) => setForm({ ...form, message_body: e.target.value })}
                required
              />
            </div>
            <div className="col-md-4">
              <VariableList
                type={form.message_type}
                onPick={(variable) => setForm({ ...form, message_body: form.message_body + variable })}
              />
            </div>
          </div>
        </div>

        <hr className="dotted" />
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className="btn btn-primary submit-new-field"
            disabled={form.message_body.trim() === '' || form.message_type === 0 || save.isPending}
          >
            {editing ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export function ChatMessagesPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(chatMessagesShellQuery())
  const [company, setCompany] = useState(0)
  const [language, setLanguage] = useState(0)
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState<ChatMessage | null>(null)
  const [preview, setPreview] = useState<string | null>(null)

  useEffect(() => {
    if (language === 0 && shell.data && shell.data.languages.length > 0) {
      setLanguage(shell.data.languages[0].id)
    }
  }, [shell.data, language])

  const list = useQuery(chatMessagesQuery(company, language))

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'chat_languages_messages'] })
  }

  const remove = useMutation({
    mutationFn: (id: number) => removeChatMessage(id),
    onSuccess: () => {
      toast('Message removed.', 'Languages')
      reload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  const flip = useMutation({
    mutationFn: (v: { type: number; on: boolean }) => setChatMessagesActive(v.type, language, v.on),
    onSuccess: reload,
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed what="Messages & Languages" error={shell.error} onRetry={() => void shell.refetch()} />
  }
  if (!shell.data) return <Spinner />

  const messages = list.data?.messages ?? []
  const active = list.data?.message_active ?? []

  return (
    <div>
      <h4>
        <strong>Languages</strong>
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage chat <strong>message language</strong> settings.
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create new message
      </button>
      <hr />

      <div className="row" style={{ marginBottom: 20 }}>
        <div className="col-md-12">
          <form className="form-inline" id="filter_language_source" onSubmit={(e) => e.preventDefault()}>
            <div className="form-group col-md-3">
              <label>Company</label>
              <select
                className="form-control input-sm"
                name="company_settings"
                value={company}
                onChange={(e) => setCompany(Number(e.target.value))}
              >
                <option value={0}>All companies</option>
                {shell.data.companies.map((row) => (
                  <option key={row.ID} value={row.ID}>
                    {row.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group col-md-2">
              <label>Language</label>
              <select
                className="form-control input-sm"
                name="language_settings"
                value={language}
                onChange={(e) => setLanguage(Number(e.target.value))}
              >
                {shell.data.languages.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.language_name}
                  </option>
                ))}
              </select>
            </div>
          </form>
        </div>
      </div>

      <div className="list-greetings">
        {list.error ? (
          <LoadFailed what="The messages" error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data ? (
          <Spinner />
        ) : (
          active.map((type) => (
            <div key={type.id}>
              <h4>
                <strong>{type.type_name}</strong>
              </h4>
              <div className="row">
                <div className="col-sm-2">
                  <label className="color" style={{ marginBottom: 0 }}>
                    Set {type.type_name}
                  </label>
                </div>
                <div className="col-sm-10">
                  <div className="change_message_active pull-right" style={{ marginLeft: 20 }}>
                    <span>
                      <b>{type.active === 1 ? 'Activate' : 'Deactivate'}</b>
                    </span>{' '}
                    <input
                      type="checkbox"
                      name="message_active"
                      checked={type.active === 1}
                      onChange={(e) => flip.mutate({ type: type.id, on: e.target.checked })}
                    />
                  </div>
                </div>
              </div>
              <hr />

              {messages
                .filter((m) => m.message_type === type.id)
                .map((message) => (
                  <section className="settings" key={message.id}>
                    <div className="row">
                      <div className="col-md-10" style={{ wordWrap: 'break-word' }}>
                        <div>{excerpt(message.message)}</div>
                        <a
                          href="#"
                          className="preview-template color-blue"
                          onClick={(e) => {
                            e.preventDefault()
                            setPreview(message.message)
                          }}
                        >
                          <i className="fas fa-search-plus" aria-hidden="true"></i> Preview message body
                        </a>
                      </div>
                      <div className="col-md-2 aright">
                        <button className="btn btn-xs btn-primary" type="button" onClick={() => setEdited(message)}>
                          Edit
                        </button>{' '}
                        <button
                          className="btn btn-xs btn-danger remove-message"
                          type="button"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(message.id)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    <hr className="dotted" />
                  </section>
                ))}
            </div>
          ))
        )}
      </div>

      <MessageModal
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={reload}
        companies={shell.data.companies}
        languages={shell.data.languages}
        types={shell.data.messages_type}
      />
      <MessageModal
        message={edited ?? undefined}
        open={edited !== null}
        onClose={() => setEdited(null)}
        onSaved={reload}
        companies={shell.data.companies}
        languages={shell.data.languages}
        types={shell.data.messages_type}
      />

      <Modal
        id="previewModal"
        labelledBy="previewModalLabel"
        open={preview !== null}
        onClose={() => setPreview(null)}
        title="Message body"
      >
        {/* Textul se arata ca TEXT, nu ca HTML: in portal se punea cu
            `data-message` si se scria in pagina asa cum era. */}
        <div style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>{preview ?? ''}</div>
      </Modal>
    </div>
  )
}
